/**
 * D&D Beyond esintili, TIKLANABILIR karakter kağıdı.
 *
 * Tasarım kararı: sheet'teki HER sayı zarlanabilir. Bunu ayrı bir "zar at"
 * modu olmaktan çıkarıp sayının kendisini düğmeye çevirerek yapıyoruz —
 * masada hızlı kullanım sağlar, ayrıca mobilde de çalışır.
 *
 * Hesaplamaların TAMAMI `packages/rules` tarafından yapılır; bu bileşen
 * yalnızca hesaplanmış sonuçları gösterir ve kullanıcı eylemlerini üst'e
 * bildirir. Böylece aynı sheet web, masaüstü ve sunucuda birebir aynıdır.
 */

import { ABILITY_NAMES_TR, type AbilityKey, type ComputedSheet } from '@tttrpg/rules'
import { useState } from 'react'
import { Panel, RollBadge, Row, StatBox, Tag } from './primitives'
import { rollAttack, rollCheck, rollFormulaAndPush, signed } from './rollStore'

export interface SheetIdentity {
  name: string
  nickname?: string
  alignment: string
  className: string
  raceName: string
  backgroundName: string
  rank?: string
  oath?: string
  playerNote?: string
}

export interface SheetSpells {
  cantrips: { id: string; name: string; source: string; ready: boolean }[]
  prepared: {
    id: string
    name: string
    level: number
    concentration: boolean
    slot: string
  }[]
  pools: { id: string; name: string; current: number; max: number; recharge: string }[]
}

export interface CharacterSheetProps {
  identity: SheetIdentity
  sheet: ComputedSheet
  currentHp: number
  tempHp: number
  spells: SheetSpells
  /**
   * DUZENLEME GERI CAGIRMASI - HEPSI ISTEGE BAGLIDIR.
   *
   * Bir tanesi bile verilmezse sheet SALT-OKUNUR moduna gecer: can
   * kontrolleri, sayi girisleri ve havuz kaydiricilari gizlenir.
   *
   * Neden opsiyonel: paylasim baglantisi ile bakilan karakter oyuncunun
   * DEGILDIR. "Kimse benim canimi duzenleyemez" dediginde arayuz bu
   * kontrolu gizlemekle soyler; sunucu tarafinda da yazma reddedilir.
   * Iki katmanli koruma: gizlemek (konfor) + reddetmek (guvenlik).
   */
  onDamage?: (amount: number) => void
  onHeal?: (amount: number) => void
  onSetHp?: (value: number) => void
  onSetTempHp?: (value: number) => void
  onSetPool?: (id: string, value: number) => void
  /**
   * Irk secimi degisikligi. `null` verilirse panel salt-okunur gosterilir
   * (sihirbaz eklenene kadar).
   */
  onToggleChoice?: (groupId: string, optionId: string) => void
}

const ABILITY_ORDER: AbilityKey[] = ['str', 'dex', 'con', 'int', 'wis', 'cha']

export function CharacterSheet(props: CharacterSheetProps) {
  const { identity, sheet, spells } = props
  const maxHp = sheet.defense.hitPoints
  const [targetAc, setTargetAc] = useState(15)

  // Duzenleme geri cagrimalarindan en az biri varsa yazilabiliriz.
  const readOnly =
    !props.onDamage && !props.onHeal && !props.onSetHp && !props.onSetTempHp && !props.onSetPool
  const hpPct = maxHp > 0 ? (props.currentHp / maxHp) * 100 : 0
  const hpTone = hpPct > 50 ? 'bg-vital' : hpPct > 25 ? 'bg-warn' : 'bg-blood'

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-3 pb-24 md:p-6">
      {/* ================= BAŞLIK ================= */}
      <header className="panel p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl text-ink md:text-3xl">
              {identity.name}
              {identity.nickname && (
                <span className="ml-2 text-base text-ink-faint">“{identity.nickname}”</span>
              )}
            </h1>
            <p className="mt-0.5 text-sm text-ink-muted">
              {identity.raceName} · {identity.className} · {identity.backgroundName} ·{' '}
              {identity.alignment}
            </p>
            {(identity.rank || identity.oath) && (
              <p className="mt-0.5 text-xs text-gold-dim">
                {identity.rank}
                {identity.rank && identity.oath ? ' · ' : ''}
                {identity.oath}
              </p>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-[0.6rem] tracking-[0.16em] text-ink-faint uppercase">Seviye</div>
              <div className="tnum font-display text-3xl font-semibold text-gold">
                {sheet.level}
              </div>
            </div>
            <div className="h-10 w-px bg-line-soft" />
            <div className="text-right">
              <div className="text-[0.6rem] tracking-[0.16em] text-ink-faint uppercase">
                Tecrube
              </div>
              <div className="tnum text-sm text-ink-muted">{sheet.xp} XP</div>
            </div>
          </div>
        </div>

        {identity.playerNote && (
          <p className="mt-3 border-t border-line-soft pt-3 text-sm text-ink-faint italic">
            {identity.playerNote}
          </p>
        )}

        {sheet.warnings.length > 0 && (
          <div className="mt-3 space-y-1 border-t border-line-soft pt-3">
            {sheet.warnings.map((w) => (
              <div key={w} className="text-xs text-warn">
                ⚠ {w}
              </div>
            ))}
          </div>
        )}
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,290px)_minmax(0,1fr)]">
        {/* ================= SOL SÜTUN ================= */}
        <aside className="space-y-4">
          <Panel title="Yetenekler">
            <div className="grid grid-cols-2 gap-2">
              {ABILITY_ORDER.map((key) => {
                const a = sheet.abilities[key]
                return (
                  <div
                    key={key}
                    className="rounded border border-line-soft bg-surface-2 p-2 text-center"
                  >
                    <div className="text-[0.6rem] tracking-[0.14em] text-ink-faint uppercase">
                      {ABILITY_NAMES_TR[key]}
                    </div>
                    <div className="tnum font-display text-2xl font-semibold text-ink">
                      {a.score}
                    </div>
                    <RollBadge
                      value={a.modifier}
                      title={`${ABILITY_NAMES_TR[key]} beceri atışı — 1d20 ${signed(a.modifier)}`}
                      onClick={() =>
                        rollCheck(`${ABILITY_NAMES_TR[key]} Kontrol`, a.modifier, {
                          subtitle: `1d20 ${signed(a.modifier)}`,
                        })
                      }
                    />
                    <div className="tnum mt-1 text-[0.6rem] text-ink-faint">
                      kurtarma {signed(a.saveBonus)}
                    </div>
                  </div>
                )
              })}
            </div>
            <p className="mt-2 text-center text-[0.6rem] text-ink-faint">
              Sarı sayıya tıkla → zar atılır
            </p>
          </Panel>

          <Panel title="Kurtarma Atışları">
            {ABILITY_ORDER.map((key) => {
              const a = sheet.abilities[key]
              return (
                <Row key={key}>
                  <span className="flex items-center gap-1.5 text-sm">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${
                        a.proficient ? 'bg-gold' : 'bg-line'
                      }`}
                      aria-hidden="true"
                    />
                    <span className={a.proficient ? 'text-ink' : 'text-ink-muted'}>
                      {ABILITY_NAMES_TR[key]}
                    </span>
                  </span>
                  <RollBadge
                    value={a.saveBonus}
                    tone={a.proficient ? 'gold' : 'muted'}
                    title={`${ABILITY_NAMES_TR[key]} kurtarma atışı`}
                    onClick={() =>
                      rollCheck(`Kurtarma: ${ABILITY_NAMES_TR[key]}`, a.saveBonus, {
                        subtitle: `1d20 ${signed(a.saveBonus)}`,
                      })
                    }
                  />
                </Row>
              )
            })}
          </Panel>

          <Panel title="Algı ve Duyu">
            <Row>
              <span className="text-sm text-ink-muted">Pasif Algı</span>
              <span className="tnum font-semibold text-gold">{sheet.passivePerception}</span>
            </Row>
            <Row>
              <span className="text-sm text-ink-muted">Pasif Araştırma</span>
              <span className="tnum font-semibold text-ink">{sheet.passiveInvestigation}</span>
            </Row>
            <Row>
              <span className="text-sm text-ink-muted">Pasif Sezgi</span>
              <span className="tnum font-semibold text-ink">{sheet.passiveInsight}</span>
            </Row>
            {sheet.senses.darkvision > 0 && (
              <Row>
                <span className="text-sm text-ink-muted">Karanlık Görüş</span>
                <span className="tnum text-ink">{sheet.senses.darkvision} ft</span>
              </Row>
            )}
            <Row>
              <span className="text-sm text-ink-muted">Zarf Ölçüsü</span>
              <span className="text-ink">{sheet.size}</span>
            </Row>
            <Row>
              <span className="text-sm text-ink-muted">Menzil</span>
              <span className="tnum text-ink">{sheet.reach} ft</span>
            </Row>
            {sheet.conditions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1 border-t border-line-soft pt-2">
                {sheet.conditions.map((c) => (
                  <Tag key={c.slug} tone="blood">
                    {c.labelTr}
                  </Tag>
                ))}
              </div>
            )}
          </Panel>
        </aside>

        {/* ================= SAĞ SÜTUN ================= */}
        <main className="space-y-4">
          {/* --- Savunma satırı + HP kontrolü --- */}
          <Panel
            title="Savunma"
            right={
              <span className="text-[0.65rem] text-ink-faint">
                Hedef AC
                <input
                  type="number"
                  value={targetAc}
                  onChange={(e) => setTargetAc(Number(e.target.value) || 0)}
                  className="tnum ml-2 w-12 rounded border border-line bg-surface-3 px-1 py-0.5 text-right text-ink"
                />
              </span>
            }
          >
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
              <StatBox label="Zırh (AC)" value={sheet.defense.armorClass} tone="gold" size="lg" />
              <StatBox
                label="İnisiyatif"
                value={signed(sheet.defense.initiative)}
                onClick={() =>
                  rollCheck('İnisiyatif', sheet.defense.initiative, {
                    subtitle: `1d20 ${signed(sheet.defense.initiative)}`,
                  })
                }
              />
              <StatBox label="Hız" value={`${sheet.defense.speed} ft`} />
              <StatBox label="Yetkinlik" value={signed(sheet.proficiencyBonus)} tone="gold" />
              <StatBox
                label="Büyü DC"
                value={sheet.spellcasting.isSpellcaster ? sheet.spellcasting.saveDc : '—'}
                tone="arcane"
              />
              <StatBox
                label="Taşıma"
                value={`${sheet.weight.total}/${sheet.weight.carryCapacity}`}
                hint="lb"
                size="sm"
                tone={sheet.weight.total > sheet.weight.carryCapacity ? 'blood' : 'default'}
              />
            </div>

            {/* CAN PANELİ */}
            <div className="mt-3 rounded border border-line-soft bg-surface-2 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-[0.6rem] tracking-[0.16em] text-ink-faint uppercase">
                    Can
                  </span>
                  <span className="tnum font-display text-2xl font-semibold text-ink">
                    {props.currentHp}
                    <span className="text-ink-faint">/{maxHp}</span>
                  </span>
                  {props.tempHp > 0 && (
                    <span className="tnum text-sm text-arcane">(+{props.tempHp} geçici)</span>
                  )}
                </div>

                {!readOnly && (
                  <>
                    <Stepper
                      onClick={() => props.onDamage?.(1)}
                      disabled={props.currentHp <= 0}
                      label="−1"
                    />
                    <Stepper
                      onClick={() => props.onDamage?.(5)}
                      disabled={props.currentHp <= 0}
                      label="−5"
                    />
                    <Stepper
                      onClick={() => props.onHeal?.(1)}
                      disabled={props.currentHp >= maxHp}
                      label="+1"
                    />
                    <Stepper
                      onClick={() => props.onHeal?.(5)}
                      disabled={props.currentHp >= maxHp}
                      label="+5"
                    />
                    <button
                      type="button"
                      onClick={() => props.onSetHp?.(maxHp)}
                      disabled={props.currentHp >= maxHp}
                      aria-label="Canı tamamen doldur"
                      className="ml-1 cursor-pointer rounded border border-vital/40 bg-vital/10 px-2 py-1 text-[0.7rem] text-vital transition-colors hover:bg-vital/20 disabled:cursor-not-allowed disabled:opacity-35"
                    >
                      dolu
                    </button>
                  </>
                )}
              </div>

              {/* Can barı */}
              <div className="mt-2 h-3 overflow-hidden rounded-full bg-void">
                <div
                  className={`h-full ${hpTone} transition-[width] duration-300 ease-out`}
                  style={{ width: `${Math.max(0, Math.min(100, hpPct))}%` }}
                />
              </div>

              {/* Elle giriş + geçici HP */}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                {!readOnly && (
                  <>
                    <label className="flex items-center gap-1 text-ink-faint">
                      can
                      <input
                        type="number"
                        min={0}
                        max={maxHp}
                        value={props.currentHp}
                        onChange={(e) =>
                          props.onSetHp?.(Math.max(0, Math.min(maxHp, Number(e.target.value) || 0)))
                        }
                        className="tnum w-14 rounded border border-line bg-surface-3 px-1 py-0.5 text-right text-ink"
                      />
                    </label>
                    <label className="flex items-center gap-1 text-ink-faint">
                      geçici
                      <input
                        type="number"
                        min={0}
                        value={props.tempHp}
                        onChange={(e) =>
                          props.onSetTempHp?.(Math.max(0, Number(e.target.value) || 0))
                        }
                        className="tnum w-14 rounded border border-line bg-surface-3 px-1 py-0.5 text-right text-ink"
                      />
                    </label>
                  </>
                )}
                <span className="tnum text-ink-faint">
                  can küpeleri: 1d10 · taşıma {sheet.weight.total} lb
                </span>
              </div>

              {/* ÖZEL KAYNAKLAR (KP) — can barının YANINDA, aynı panel içinde.
                  Neden ayrı bir bölüm değil: vampir KP'si kanla ilgili ve masada
                  canla birlikte okunur. Ayrı bir panelde gizli kalırdı. */}
              {sheet.resources.length > 0 && (
                <div className="mt-3 space-y-2 border-t border-line-soft pt-3">
                  {sheet.resources.map((resource) => {
                    const pool = spells.pools.find((p) => p.id === resource.id)
                    // Azami degerin OTORITESI motordur (`resource.max`), havuz
                    // yalnizca ANLIK degeri tutar. Havuzdaki `max` eski bir
                    // seviyeden kalmis olabilir; motor her render'da yeniden
                    // hesaplar ve seviye artinca bar buyur.
                    const current = Math.min(pool?.current ?? 0, resource.max)
                    const max = resource.max
                    return (
                      <div key={resource.id} className="flex flex-wrap items-center gap-2">
                        <span className="text-[0.6rem] tracking-[0.16em] text-arcane uppercase">
                          {resource.name}
                        </span>
                        <span className="tnum font-display text-lg font-semibold text-ink">
                          {current}
                          <span className="text-ink-faint">/{max}</span>
                        </span>
                        {!readOnly && (
                          <>
                            <Stepper
                              onClick={() =>
                                props.onSetPool?.(resource.id, Math.max(0, current - 5))
                              }
                              disabled={current <= 0}
                              label="−5"
                            />
                            <Stepper
                              onClick={() =>
                                props.onSetPool?.(resource.id, Math.min(max, current + 5))
                              }
                              disabled={current >= max}
                              label="+5"
                            />
                            <button
                              type="button"
                              onClick={() => props.onSetPool?.(resource.id, max)}
                              disabled={current >= max}
                              aria-label={`${resource.name} tamamen doldur`}
                              className="cursor-pointer rounded border border-arcane/40 bg-arcane/10 px-2 py-1 text-[0.7rem] text-arcane transition-colors hover:bg-arcane/20 disabled:cursor-not-allowed disabled:opacity-35"
                            >
                              dolu
                            </button>
                          </>
                        )}
                        {/* KP yuvarları — azami küçük olduğu için nokta olarak çizilir.
                            `index` key burada DOĞRU: noktaların kimliği yok, sıraları
                            yok, durumları yok. Konumları değişmez. Kimlik vermek
                            onlara var olmayan bir kimlik uydururdu. */}
                        <span className="flex items-center gap-0.5" aria-hidden="true">
                          {Array.from({ length: max }, (_, i) => (
                            <span
                              // biome-ignore lint/suspicious/noArrayIndexKey: konumsal nokta, kimliksiz
                              key={`kp-dot-${i}`}
                              className={`h-2 w-2 rounded-full ${
                                i < current ? 'bg-arcane' : 'bg-line'
                              }`}
                            />
                          ))}
                        </span>
                        {resource.recharge !== 'manual' && (
                          <span className="text-[0.65rem] text-ink-faint">
                            {resource.recharge === 'long' ? 'uzun dinlenme' : 'kısa dinlenme'}
                            de dolar
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </Panel>

          {/* --- YOLLARIM --- */}
          {/* Yollar burada gorunur cunku oyuncu "karakterim ne kadar guclu?"
               sorusunu ozellik listesinde degil, yol adlarinda sorar. */}
          {sheet.paths.length > 0 && (
            <Panel title={`Yollarım (${sheet.paths.length})`}>
              <div className="space-y-4">
                {sheet.paths.map((path) => (
                  <div key={path.id} className="border-l-2 border-arcane/40 pl-3">
                    <div className="text-sm font-semibold text-ink">{path.name}</div>
                    {path.description && (
                      <p className="mt-0.5 text-xs text-ink-faint">{path.description}</p>
                    )}
                    <ul className="mt-2 space-y-1">
                      {path.activeFeatures.map((feature) => (
                        <li key={feature.id} className="flex gap-2 text-xs">
                          <span className="tnum shrink-0 text-gold">
                            {feature.grantedAtLevel}. sv
                          </span>
                          <span className="text-ink">{feature.name}</span>
                        </li>
                      ))}
                      {path.lockedFeatures.map((feature) => (
                        <li key={feature.id} className="flex gap-2 text-xs opacity-55">
                          <span className="tnum shrink-0 text-ink-faint">
                            {feature.grantedAtLevel}. sv
                          </span>
                          <span className="text-ink-faint line-through decoration-line">
                            {feature.name}
                          </span>
                          <span className="shrink-0 text-[0.6rem] text-ink-faint">
                            (henüz açılmadı)
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {/* --- Beceriler --- */}
          <Panel title="Beceriler">
            <div className="grid gap-x-6 sm:grid-cols-2">
              {sheet.skills.map((s) => (
                <Row key={s.key}>
                  <span className="flex items-center gap-1.5 text-sm">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${
                        s.proficient ? 'bg-gold' : 'bg-line'
                      }`}
                      aria-hidden="true"
                    />
                    <span className={s.proficient ? 'text-ink' : 'text-ink-muted'}>
                      {s.labelTr}
                    </span>
                    <span className="text-[0.6rem] text-ink-faint uppercase">
                      ({ABILITY_NAMES_TR[s.ability].slice(0, 3)})
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    {s.proficient && (
                      <span className="tnum text-[0.6rem] text-ink-faint">pasif {s.passive}</span>
                    )}
                    <RollBadge
                      value={s.modifier}
                      tone={s.proficient ? 'gold' : 'muted'}
                      title={`${s.labelTr} — 1d20 ${signed(s.modifier)}`}
                      onClick={() =>
                        rollCheck(s.labelTr, s.modifier, {
                          subtitle: `1d20 ${signed(s.modifier)}`,
                        })
                      }
                    />
                  </span>
                </Row>
              ))}
            </div>
          </Panel>

          {/* --- Saldırılar --- */}
          <Panel title="Saldırılar">
            {sheet.attacks.length === 0 ? (
              <p className="text-sm text-ink-faint">Silah yok.</p>
            ) : (
              <div className="space-y-2">
                {sheet.attacks.map((a) => (
                  <button
                    key={a.itemId}
                    type="button"
                    onClick={() =>
                      rollAttack({
                        label: a.name,
                        attackBonus: a.attackBonus,
                        damageDice: a.damageDice,
                        damageBonus: a.damageBonus,
                        damageType: a.damageType,
                        targetAc,
                      })
                    }
                    className="flex w-full cursor-pointer items-center gap-3 rounded border border-line-soft bg-surface-2 px-3 py-2 text-left transition-colors hover:border-gold-dim hover:bg-surface-3"
                  >
                    <span className="tnum font-display text-lg font-semibold text-gold-bright">
                      {signed(a.attackBonus)}
                    </span>
                    <span className="flex-1">
                      <span className="block text-sm text-ink">{a.name}</span>
                      <span className="tnum block text-xs text-ink-muted">
                        {a.damageDice}
                        {signed(a.damageBonus)} {a.damageType}
                        {a.isVersatile && a.versatileDamageDice
                          ? ` · çift tutuş ${a.versatileDamageDice}${signed(a.damageBonus)}`
                          : ''}
                      </span>
                    </span>
                    {a.isRanged && (
                      <Tag>
                        {a.rangeShort}/{a.rangeLong} ft
                      </Tag>
                    )}
                  </button>
                ))}
                <p className="text-[0.65rem] text-ink-faint">
                  Saldırıya tıkla → isabet zarı + hasar atılır (hedef AC {targetAc})
                </p>
              </div>
            )}
          </Panel>

          {/* --- Irk secimleri --- */}
          {sheet.choices.length > 0 && (
            <Panel title="Irk Seçimleri">
              <div className="space-y-4">
                {sheet.choices.map((choice) => {
                  const selectedIds = new Set(choice.selected.map((s) => s.id))

                  // Liste bekleniyor: seçenek dizisi henüz yazılmamış.
                  // Oyuncu hatası DEĞİLDİR — bu içerik eksiğidir.
                  if (choice.pending) {
                    return (
                      <div key={choice.groupId}>
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <span className="text-sm text-ink-muted">{choice.groupLabelTr}</span>
                          <span className="text-[0.65rem] text-warn">liste bekleniyor</span>
                        </div>
                        <p className="rounded border border-line-soft border-dashed bg-surface-2 px-2 py-1.5 text-[0.7rem] text-ink-faint">
                          Bu seçimin seçenek listesi henüz eklenmedi. Yapı hazır — liste
                          tanımlandığında otomatik olarak burada görünecek.
                        </p>
                      </div>
                    )
                  }

                  return (
                    <div key={choice.groupId}>
                      <div className="mb-1.5 flex items-center justify-between gap-2">
                        <span className="text-sm text-ink">{choice.groupLabelTr}</span>
                        {choice.unlocked ? (
                          <span
                            className={`tnum text-[0.65rem] ${
                              choice.complete ? 'text-vital' : 'text-warn'
                            }`}
                          >
                            {choice.chosen}/{choice.required} seçildi
                          </span>
                        ) : (
                          <span className="text-[0.65rem] text-ink-faint">seviye kilitli</span>
                        )}
                      </div>
                      {choice.unlocked && !choice.complete && (
                        <p className="mb-2 text-[0.65rem] text-warn">
                          Bu seçim yapılmadan özelliğin etkileri uygulanmaz.
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        {choice.available.map((option) => {
                          const active = selectedIds.has(option.id)
                          const interactive = Boolean(props.onToggleChoice) && choice.unlocked
                          return (
                            <button
                              key={option.id}
                              type="button"
                              disabled={!interactive}
                              onClick={() => props.onToggleChoice?.(choice.groupId, option.id)}
                              className={`rounded border px-2 py-1 text-xs transition-colors ${
                                active
                                  ? 'border-gold bg-gold/20 text-gold-bright'
                                  : 'border-line-soft bg-surface-2 text-ink-muted'
                              } ${interactive ? 'cursor-pointer hover:border-gold-dim' : 'cursor-default'}`}
                            >
                              {option.nameTr}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </Panel>
          )}

          {/* --- Büyüler --- */}
          {sheet.spellcasting.isSpellcaster && (
            <Panel
              title="Büyüler"
              right={
                <span className="text-[0.65rem] text-ink-faint">
                  yetenek: {ABILITY_NAMES_TR[sheet.spellcasting.ability]} · DC{' '}
                  {sheet.spellcasting.saveDc} · saldırı {signed(sheet.spellcasting.attackBonus)}
                </span>
              }
            >
              {/* Yuvarlar */}
              {sheet.spellcasting.slots.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {sheet.spellcasting.slots.map((slot) => (
                    <SlotTrack
                      key={`track-${slot.level}`}
                      slotLevel={slot.level}
                      total={slot.total}
                    />
                  ))}
                </div>
              )}

              {/* Cantrip'ler */}
              <div className="mb-3">
                <div className="mb-1 text-[0.65rem] tracking-[0.14em] text-ink-faint uppercase">
                  Cantrip'ler ({spells.cantrips.length})
                </div>
                <div className="grid gap-1 sm:grid-cols-2">
                  {spells.cantrips.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() =>
                        rollFormulaAndPush(c.name, c.id === 'sacred-flame' ? '1d8' : '1', c.source)
                      }
                      className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1 text-left text-sm text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
                    >
                      <span className="truncate">{c.name}</span>
                      <span className="shrink-0 text-[0.6rem] text-ink-faint">{c.source}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Hazırlanmış büyüler */}
              <div>
                <div className="mb-1 text-[0.65rem] tracking-[0.14em] text-ink-faint uppercase">
                  Hazırlanmış büyüler ({spells.prepared.length}/{sheet.spellcasting.preparedCount})
                </div>
                <div className="grid gap-1 sm:grid-cols-2">
                  {spells.prepared.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => rollFormulaAndPush(p.name, '1d8+3', `${p.slot}. seviye yuva`)}
                      className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1 text-left text-sm text-ink transition-colors hover:bg-surface-2"
                    >
                      <span className="truncate">{p.name}</span>
                      <span className="flex shrink-0 gap-1">
                        {p.concentration && <Tag tone="arcane">kons.</Tag>}
                        <Tag tone="gold">{p.slot}</Tag>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </Panel>
          )}

          {/* --- Yenilenen havuzlar (Yağma, Tanrısal Algı) --- */}
          {spells.pools.length > 0 && (
            <Panel title="Kullanım Havuzları">
              <div className="space-y-3">
                {spells.pools.map((pool) => (
                  <PoolTrack key={pool.id} pool={pool} onSet={props.onSetPool} />
                ))}
              </div>
            </Panel>
          )}

          {/* --- Dogal silahlar --- */}
          {sheet.naturalWeapons.length > 0 && (
            <Panel title="Doğal Silahlar">
              <div className="space-y-2">
                {sheet.naturalWeapons.map((w) => (
                  <button
                    key={w.itemId}
                    type="button"
                    onClick={() =>
                      rollAttack({
                        label: w.name,
                        attackBonus: w.attackBonus,
                        damageDice: w.damageDice,
                        damageBonus: w.damageBonus,
                        damageType: w.damageType,
                        targetAc,
                      })
                    }
                    className="flex w-full cursor-pointer items-center gap-3 rounded border border-line-soft bg-surface-2 px-3 py-2 text-left transition-colors hover:border-gold-dim hover:bg-surface-3"
                  >
                    <span className="tnum font-display text-lg font-semibold text-gold-bright">
                      {signed(w.attackBonus)}
                    </span>
                    <span className="flex-1">
                      <span className="block text-sm text-ink">{w.name}</span>
                      <span className="tnum block text-xs text-ink-muted">
                        {w.damageDice}
                        {signed(w.damageBonus)} {w.damageType}
                        {w.notes.length > 0 && ` · ${w.notes.join(', ')}`}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </Panel>
          )}

          {/* --- Yuva harcamadan kullanilan buyuler --- */}
          {sheet.innateSpells.length > 0 && (
            <Panel title="Irk Büyüleri (yuva harcamaz)">
              <ul className="space-y-1.5">
                {sheet.innateSpells.map((grant) => (
                  <li key={grant.spellIds.join('-')} className="flex items-baseline gap-2">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-arcane" />
                    <span className="text-sm text-ink">{grant.spellIds.length} büyü</span>
                    <span className="text-[0.6rem] text-ink-faint">
                      {grant.uses.count}/
                      {grant.uses.recharge === 'long' ? 'Uzun Dinlenme' : 'Kısa Dinlenme'} · seviye{' '}
                      {grant.grantedAtLevel}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          {/* --- Özellikler --- */}
          <Panel title="Özellikler ve Yetenekler">
            {sheet.features.length === 0 ? (
              <p className="text-sm text-ink-faint">Özellik yok.</p>
            ) : (
              <ul className="space-y-1.5">
                {sheet.features.map((f) => (
                  <li key={`${f.source}-${f.id}`} className="flex items-baseline gap-2">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-dim" />
                    <span className="text-sm text-ink">{f.name}</span>
                    <span className="text-[0.6rem] text-ink-faint">
                      {f.sourceName}
                      {f.level ? ` · sv. ${f.level}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {(sheet.resistances.damageResistances.length > 0 ||
              sheet.resistances.damageImmunities.length > 0 ||
              sheet.resistances.damageVulnerabilities.length > 0 ||
              sheet.resistances.conditionImmunities.length > 0) && (
              <div className="mt-3 flex flex-wrap gap-1.5 border-t border-line-soft pt-3">
                {sheet.resistances.damageResistances.map((r) => (
                  <Tag key={`r-${r}`} tone="vital">
                    direnç: {r}
                  </Tag>
                ))}
                {sheet.resistances.damageImmunities.map((r) => (
                  <Tag key={`i-${r}`} tone="vital">
                    bağışıklık: {r}
                  </Tag>
                ))}
                {sheet.resistances.damageVulnerabilities.map((r) => (
                  <Tag key={`v-${r}`} tone="blood">
                    zayıflık: {r}
                  </Tag>
                ))}
                {sheet.resistances.conditionImmunities.map((r) => (
                  <Tag key={`c-${r}`} tone="arcane">
                    koşul bağışıklığı: {r}
                  </Tag>
                ))}
              </div>
            )}
          </Panel>

          {/* --- Envanter --- */}
          <Panel title="Envanter">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line-soft text-left text-[0.65rem] tracking-[0.12em] text-ink-faint uppercase">
                  <th className="pb-1 font-normal">Eşya</th>
                  <th className="pb-1 text-right font-normal">Adet</th>
                  <th className="pb-1 text-right font-normal">Ağırlık</th>
                  <th className="pb-1 text-right font-normal">Durum</th>
                </tr>
              </thead>
              <tbody>
                {sheet.inventory.map((line) => (
                  <tr key={line.itemId} className="border-b border-line-soft/50 last:border-0">
                    <td className="py-1.5 text-ink">
                      {line.name}
                      {line.slot && (
                        <span className="ml-1.5 text-[0.6rem] text-ink-faint">({line.slot})</span>
                      )}
                    </td>
                    <td className="tnum py-1.5 text-right text-ink-muted">{line.quantity}</td>
                    <td className="tnum py-1.5 text-right text-ink-muted">
                      {line.weightEach * line.quantity}
                    </td>
                    <td className="py-1.5 text-right">
                      {line.requiresAttunement ? (
                        <Tag tone={line.attuned ? 'gold' : undefined}>
                          {line.attuned ? 'odaklı' : 'odaklanmadı'}
                        </Tag>
                      ) : (
                        <span className="text-[0.65rem] text-ink-faint">
                          {line.equipped ? 'kuşanıldı' : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-line">
                  <td className="pt-1.5 text-sm text-ink-muted">Toplam</td>
                  <td />
                  <td className="tnum pt-1.5 text-right text-ink">{sheet.weight.total} lb</td>
                  <td className="tnum pt-1.5 text-right text-[0.65rem] text-ink-faint">
                    /{sheet.weight.carryCapacity} lb
                  </td>
                </tr>
              </tfoot>
            </table>
          </Panel>

          {/* --- Kaynak --- */}
          <p className="px-1 text-center text-[0.65rem] text-ink-faint">
            Hesaplanan değerler: <span className="text-ink-muted">@tttrpg/rules</span> (saf kural
            motoru). Karakter verisi: <span className="text-ink-muted">@tttrpg/content</span>.
          </p>
        </main>
      </div>
    </div>
  )
}

function Stepper({
  onClick,
  disabled,
  label,
}: {
  onClick: () => void
  disabled?: boolean
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`${label} puan`}
      className="tnum cursor-pointer rounded border border-line bg-surface-3 px-2 py-1 text-xs text-ink-muted transition-colors hover:border-gold-dim hover:text-gold active:scale-95 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-line disabled:hover:text-ink-muted"
    >
      {label}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Alt bileşenler
//
// NOT: Konumsal diziler (büyü yuvaları, havuz noktaları) burada JSX içinde
// `Array.from(..., (_, i) => <el key={i}>)` olarak yazılmaz. Kimlik, bileşen
// GÖVDESİNDE üretilen gerçek bir tanımlayıcıdır; `index` yalnızca ekranda
// gösterilen sıra numarasıdır. Böylece React yeniden sıralamada yanlış
// eşleşme yapmaz ve dizin-anahtarı tuzağına düşülmez.
// ---------------------------------------------------------------------------

interface Circle {
  id: string
  /** Ekranda gösterilen 1 tabanlı sıra. */
  ordinal: number
}

function buildCircles(prefix: string, count: number): Circle[] {
  const circles: Circle[] = []
  for (let ordinal = 1; ordinal <= count; ordinal++) {
    circles.push({ id: `${prefix}-${ordinal}`, ordinal })
  }
  return circles
}

function SlotTrack({ slotLevel, total }: { slotLevel: number; total: number }) {
  const circles = buildCircles(`slot-l${slotLevel}`, total)

  return (
    <div className="flex items-center gap-1.5 rounded border border-line-soft bg-surface-2 px-2 py-1">
      <span className="tnum text-xs text-ink-muted">{slotLevel}. seviye</span>
      {circles.map((circle) => (
        <button
          key={circle.id}
          type="button"
          title={`${slotLevel}. seviye yuva ${circle.ordinal} — harcamak için tıkla`}
          onClick={() =>
            rollFormulaAndPush(
              `${slotLevel}. Seviye Yuvası`,
              `1d${4 + slotLevel * 2}`,
              `${slotLevel}. seviye yuva kullanıldı`,
            )
          }
          className="h-3.5 w-3.5 cursor-pointer rounded-full border border-gold-dim bg-gold/50 transition-colors hover:border-blood hover:bg-blood"
        />
      ))}
    </div>
  )
}

function PoolTrack({
  pool,
  onSet,
}: {
  pool: SheetSpells['pools'][number]
  /** Yoksa havuz salt-okunur gosterilir (paylasim gorunumu). */
  onSet?: (id: string, value: number) => void
}) {
  const circles = buildCircles(`pool-${pool.id}`, pool.max)

  // Salt-okunurda imleç ve vurgu kalkar: dokunulabilir görünmemeli.
  const pointer = onSet ? 'cursor-pointer' : ''
  const hoverFull = onSet ? 'hover:border-blood hover:bg-blood' : ''
  const hoverEmpty = onSet ? 'hover:border-gold-dim' : ''

  return (
    <div className="flex items-center gap-3">
      <span className="w-32 shrink-0 text-sm text-ink">{pool.name}</span>
      <div className="flex flex-1 flex-wrap gap-1">
        {circles.map((circle) => (
          <button
            key={circle.id}
            type="button"
            title={`${pool.name} ${circle.ordinal}/${pool.max} — tıkla: kullan / iade et`}
            onClick={() =>
              onSet?.(pool.id, pool.current > circle.ordinal ? circle.ordinal : pool.max)
            }
            className={`h-5 w-5 rounded-full border transition-colors ${pointer} ${
              circle.ordinal <= pool.current
                ? `border-gold bg-gold/70 ${hoverFull}`
                : `border-line bg-surface-3 ${hoverEmpty}`
            }`}
          />
        ))}
      </div>
      <span className="tnum w-14 shrink-0 text-right text-sm text-ink-muted">
        {pool.current}/{pool.max}
      </span>
    </div>
  )
}
